import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { loadCompanyAccesses } from "../services/companyaccess.api";
import { getAllCompanies } from "../services/company.api";
import { getMaintenanceDrag } from "../services/maintenance.api";
import { useCompanyStore } from "../states/store/Company";
import { useAppStore } from "../states/store/";
import {
    DeleteFilePurchaseOrder,
  awsPresignedUrls,
  getPurchaseOrders,
} from "../services/purchaseOrder.api";
import { getStockByProductId } from "../services/stock.api";
import { changeStatusPurchaseRequisition } from "../services/purchaseRequisition.api";
import { STATUS_ITEM } from "@/types/purchaseRequisition.type";
import { getAllSuppliers } from "../services/supplier.api";
import { useMemo } from "react";
import { getLeads, updateContactsLabelVibechat } from "../services/leads.api";
import { createMessageTemplate, deleteMessageTemplates, getAllMessageTemplates } from "../services/marketing.api";
import { createCampaign, deleteCampaign, getAllCampaigns, updateCampaign } from "../services/campaigns.api";
import { ICampaign } from "@/types/campaign.type";
import { IMessagesTemplate } from "@/types/messages-template.type";
import { useToastContext } from "../components/ui/toast/toast.context";
import { validatePuxaCapivara } from "../services/netrin.api";

export const CACHE_TIMES = {
  ONE_MINUTE: 1000 * 60,
  FIVE_MINUTES: 1000 * 60 * 5,
};


type MessageComponent = {
  type: string;
  text: string;
};

type MessageTemplate = {
  name: string;
  id: string;
  components: MessageComponent; 
};

type ApiResponse = {
  ok: boolean;
  data: MessageTemplate[];
  message: string;
  cursor: string;
};

export const useGetCompanyAccesses = (email?: string) => {
  const hasUserAndCompanyAccesses = Boolean(
    !localStorage.getItem("companyAccess") && email
  );

  return useQuery({
    queryKey: ["companyAccess", email],
    queryFn: async () => {
      const result = await loadCompanyAccesses(email);
      if (!result.ok) throw new Error("Error get companyAccess");

      return result.data;
    },
    enabled: hasUserAndCompanyAccesses,
    staleTime: CACHE_TIMES.ONE_MINUTE,
    gcTime: CACHE_TIMES.FIVE_MINUTES,
  });
};

export const useGetCompanies = () => {
  return useQuery({
    queryKey: ["companies"],
    queryFn: async () => {
      const result = await getAllCompanies();
      if (!result.ok) throw new Error("Error get companies");

      return result.data;
    },
  });
};

export const useGetAllMaintenances = () => {
  const { selectedCompanies, selectedUf } = useCompanyStore((state) => ({
    selectedCompanies: state.selectedCompanies,
    selectedUf: state.selectedUf,
  }));
  const { filters } = useAppStore((state) => state.dashboard.maintenances);
  const companyIds = useMemo(
    () => selectedCompanies.map((c) => c.companyId).join(","),
    [selectedCompanies]
  );
  return useQuery({
    queryKey: ["maintenances-all", companyIds, selectedUf, filters.dateFilters],
    queryFn: async () => {
      const result = await getMaintenanceDrag({ ...filters.dateFilters });
      if (!result.ok) throw new Error("Error get all maintenaces");

      return result.data;
    },
  });
};

export const usePurchaseOrderCheck = () => {
  const queryClient = useQueryClient();

  const check = async (supplierId: string) => {
    return await queryClient.fetchQuery({
      queryKey: ["purchaseOrders", { supplierId, fields: "itens" }],
      queryFn: () => getPurchaseOrders({ supplierId, fields: "itens" }),
    });
  };

  return { check };
};

export const useGetStockProductDetails = (productId?: string) => {
  return useQuery({
    queryKey: ["stock-product-details", productId],
    queryFn: () => getStockByProductId(productId!).then((stock) => stock.data),
    enabled: !!productId,
  });
};

export const useRejectedPurchaseOrdem = () => {
  return useMutation({
    mutationFn: async ({
      requisitionId,
      status,
      reason,
    }: {
      requisitionId: string;
      status: STATUS_ITEM;
      reason: string;
    }) => {
      const response = await changeStatusPurchaseRequisition({
        requisitionId,
        reason,
        status,
      });
      if (!response.ok || !response.data) {
        throw new Error("Erro ao processar pedido de cancelamento");
      }
      return response.data;
    },
  });
};

export const useGetAllSuppliers = () => {
  return useQuery({
    queryKey: ["all-suppliers"],
    queryFn: async () => {
      const response = await getAllSuppliers();
      if (!response.ok) {
        throw new Error("Erro ao buscar fornecedores");
      }
      return response.data;
    },
    refetchOnWindowFocus: false,
  });
};

export const useGetManyLeads = (filters, enabled) => {
  const companyStore = useCompanyStore();
  return useQuery({
    queryKey: ["leads", filters, companyStore.company?._id],
    queryFn: async () => {
      const response = await getLeads({...filters, itemsPerPage: 4500});
      if (!response.ok) {
        throw new Error("Erro ao buscar leads");
      }
      return response.data;
    },
    enabled,
    refetchOnWindowFocus: false
  });
};

export const useAttachFilePresignedUrls = () => {
  return useMutation({
    mutationFn: async (files: File[]) => {
      const response = await awsPresignedUrls(files);
      if (!response.ok) {
        throw new Error("Erro ao enviar arquivo");
      }
      return response.data;
    },
  });
};

export const useLeadsUpdateLabelsVibechat = () => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useToastContext();

  return useMutation({
    mutationFn: async ({contactIds, labels}: { contactIds: string[], labels: string[]}) => {
      const response = await updateContactsLabelVibechat({contactIds, labels});
      if (!response.ok) {
        throw new Error("Erro ao atualizar contatos no vibechat");
      }

      return response;
    },
    onSuccess: () => {
      notifySuccess("Leads atualizados com etiquetas")
      queryClient.invalidateQueries({ queryKey: ["vibechat-tags"] });
    },
    onError: () => {
      notifyError('Ocorreu um erro atualizar a(s) etiqueta(s)')
    }
  });
}

export const useMarketingMessageTemplates = () => {
  return useInfiniteQuery<ApiResponse>({
    queryKey: ["marketing"],
    queryFn: async ({ pageParam }) => {
      const response = await getAllMessageTemplates(pageParam as string | undefined);
      return response?.data || []; 
    },
    getNextPageParam: (lastPage) => lastPage?.cursor,
    initialPageParam: undefined as string | undefined,
  });
};

export const useCreateMessageTemplate = () => {
  const { notifySuccess, notifyError } = useToastContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: IMessagesTemplate) => {
      const response = await createMessageTemplate(data);
      if (!response.ok) {
        throw new Error("Erro ao criar template de mensagem");
      }
      return response.data;
    },
    onSuccess: () => {
      notifySuccess("Template criado com sucesso.");
      queryClient.invalidateQueries({ queryKey: ["marketing"] });
    },
    onError: (error: Error) => {
      notifyError(error.message);
    }
  });
}

export const useUpdateMessageTemplate = () => {
  const { notifySuccess, notifyError } = useToastContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: IMessagesTemplate) => {
      const response = await createMessageTemplate(data);
      if (!response.ok) {
        throw new Error("Erro ao atualizar template");
      }
      return response.data;
    },
    onSuccess: () => {
      notifySuccess("Template atualizado com sucesso.");
      queryClient.invalidateQueries({ queryKey: ["marketing"] });
    },
    onError: (error: Error) => {
      notifyError(error.message);
    }
  });
}

export const useDeleteMessageTemplate = () => {
  const { notifySuccess, notifyError } = useToastContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const response = await deleteMessageTemplates(name);

      if (!response.ok) {
        throw new Error(`Erro ao excluir o template: ${response.message}`);
      }

      return response.data;
    },
    onSuccess: () => {
      notifySuccess("Template excluído com sucesso.");
      queryClient.invalidateQueries({ queryKey: ["marketing"] });
    },
    onError: (error: Error) => {
      notifyError(error.message);
    },
  });
};


export const useGetAllCampaigns = (pageOptions?: { search?: string; status?: string }) => {
  return useQuery({
    queryKey: ["campaigns", pageOptions],
    queryFn: async () => {
      const response = await getAllCampaigns(pageOptions);

      return response.data;
    },
    refetchOnWindowFocus: false
  });
};

export const useCreateCampaign = () => {
  const { notifySuccess, notifyError } = useToastContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Omit<ICampaign, '_id' | 'sentCount' | 'status'>) => {
      const response = await createCampaign(data);
      if (!response.ok) {
        throw new Error("Erro ao criar campanha");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      notifySuccess("Campanha criada com sucesso.")
    },
    onError: () => {
      notifyError(`Ocorreu um erro ao criar a campanha`)
    }
  });
};

export const useUpdateCampaign = () => {
  const queryClient = useQueryClient();
  const { notifySuccess, notifyError } = useToastContext();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Omit<ICampaign, '_id' | 'sentCount' | 'status'>> }) => {
      const response = await updateCampaign(id, data);
      if (!response.ok) {
        throw new Error("Erro ao atualizar campanha");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      notifySuccess("Campanha atualizada com sucesso.")
    },
    onError: () => {
      notifyError('Ocorreu um erro ao atualizar a campanha')
    }
  });
};

export const useDeleteCampaign = () => {
  const { notifySuccess, notifyError } = useToastContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteCampaign(id);

      return response;
    },
    onSuccess: () => {
      notifySuccess("Campanha excluida com sucesso.")
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: () => {
      notifyError('Ocorreu um erro ao deletar a campanha')
    }
  });
};

export const useDeleteFileOrder = () => {
  const { notifySuccess, notifyError } = useToastContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {file: string, purchaseOrderId: string}) => {
      const response = await DeleteFilePurchaseOrder(data);
      if (!response.ok) {
        throw new Error("Erro ao excluir arquivo");
      }
      return response.data;
    },
    onSuccess: (_, data) => {
      notifySuccess("Arquivo excluido com sucesso.")
      queryClient.invalidateQueries({ queryKey: ["purchaseOrder", data.purchaseOrderId] });
    },
    onError: () => {
      notifyError('Ocorreu um erro o arquivo')
    }
  });
};

export const useLegalValidation = () => {
  const { notifySuccess, notifyError } = useToastContext();

  return useMutation({
    mutationFn: async (params: { cpf: string; entityId: string, entityName: string }) =>
      validatePuxaCapivara(params.cpf, params.entityId, params.entityName),
    onSuccess: (result) => {
      if (!result.ok) {
        notifyError("Erro ao iniciar a validação jurídica");
        return;
      }
      notifySuccess("Validação iniciada. Aguarde o retorno.");
    },
    onError: () => {
      notifyError("Falha ao iniciar a validação jurídica");
    },
  });
};
